import {getSharedCookieDomain} from './shared-cookie-domain'

export type SelectedCompany = {
  id: number
  name: string
}

const LEGACY_LOCAL_STORAGE_KEY = 'app:companies:selected'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const selectedCompanySchema = {
  parse(value: unknown): SelectedCompany[] {
    if (!Array.isArray(value)) return []

    return value.flatMap((item) => {
      if (typeof item !== 'object' || item == null) return []

      const id = Number((item as SelectedCompany).id)
      const name = String((item as SelectedCompany).name ?? '')

      if (!Number.isFinite(id) || !name) return []

      return [{id, name}]
    })
  },
}

function getSelectedCompaniesCookieKey() {
  return process.env.REACT_APP_SELECTED_COMPANIES_KEY ?? '@Brain-selected-companies'
}

function readCookieValue(): string | null {
  const key = getSelectedCompaniesCookieKey()
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedKey}=([^;]*)`))

  return match?.[1] ?? null
}

function readLegacyLocalStorage(): SelectedCompany[] {
  const raw = window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY)
  if (raw == null) return []

  try {
    return selectedCompanySchema.parse(JSON.parse(raw))
  } catch {
    return []
  }
}

export function readSelectedCompanies(): SelectedCompany[] {
  const rawCookie = readCookieValue()

  if (rawCookie) {
    try {
      const parsed = selectedCompanySchema.parse(JSON.parse(decodeURIComponent(rawCookie)))
      if (parsed.length) return parsed
    } catch {
      try {
        const parsed = selectedCompanySchema.parse(JSON.parse(rawCookie))
        if (parsed.length) return parsed
      } catch {}
    }
  }

  return readLegacyLocalStorage()
}

export function writeSelectedCompanies(companies: SelectedCompany[]) {
  const key = getSelectedCompaniesCookieKey()
  const secure = window.location.protocol === 'https:'
  const domain = getSharedCookieDomain()
  const encodedValue = encodeURIComponent(JSON.stringify(companies))

  if (companies.length) {
    window.localStorage.setItem(LEGACY_LOCAL_STORAGE_KEY, JSON.stringify(companies))
  } else {
    window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY)
  }

  if (!companies.length) {
    let cookie = `${key}=; path=/; max-age=0; samesite=lax`

    if (secure) cookie += '; secure'
    if (domain) cookie += `; domain=${domain}`

    document.cookie = cookie
    return
  }

  let cookie = `${key}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`

  if (secure) cookie += '; secure'
  if (domain) cookie += `; domain=${domain}`

  document.cookie = cookie
}
